/**
 * TrueFalseQuestion.tsx
 * V-SAT 2025 True/False question with context and table format
 */

import { useTranslation } from 'react-i18next';
import { Card } from '@vsat/shared/components';
import { cn } from '@vsat/shared/lib/utils';
import type { TrueFalseSubItem, TrueFalseContext } from '../types';
import { Check } from 'lucide-react';
import { MathText } from '../../MathRenderer';
import { parseFormulas } from '../MathFormula';
import HtmlContent from '../HtmlContent';

interface Props {
  items: TrueFalseSubItem[];
  context?: TrueFalseContext;
  title?: string; // Question title to replace "Phát biểu" header
  value: Record<string, boolean>;
  onChange: (answers: Record<string, boolean>) => void;
  readOnly?: boolean;
  examStandard?: string | null; // Exam standard: 'VSAT', 'THPT', or null
  subject?: string | null;
}

export default function TrueFalseQuestion({
  items,
  context,
  title,
  value,
  onChange,
  readOnly,
  examStandard,
  subject,
}: Props) {
  const { t, i18n } = useTranslation();

  const isEnglishSubject = () => {
    if (!subject) return false;
    const normalized = subject.toLowerCase().trim();
    return normalized === 'english' || normalized === 'tiếng anh' || normalized === 'tieng anh';
  };

  const isEnglishExam = isEnglishSubject();
  const headerTrueLabel = isEnglishExam ? 'True' : t('common.questionTypes.true');
  const headerFalseLabel = isEnglishExam ? 'False' : t('common.questionTypes.false');

  const buttonTrueLabel = headerTrueLabel.charAt(0);
  const buttonFalseLabel = headerFalseLabel.charAt(0);

  const handleItemChange = (itemId: string, answer: boolean) => {
    if (readOnly) return;
    onChange({
      ...(value || {}),
      [itemId]: answer,
    });
  };

  const getAnsweredCount = () => {
    if (!value || typeof value !== 'object') {
      return 0;
    }
    return Object.keys(value).filter(key => value[key] !== undefined).length;
  };

  return (
    <div className='space-y-6'>
      <div className='flex gap-6 h-full'>
        {/* Context Section - Left Side */}
        {context && (
          <div className='w-1/2 flex-shrink-0 bg-card/80 backdrop-blur-sm p-6 rounded-lg border border-border group hover:shadow-lg transition-all duration-300 hover:ring-2 hover:ring-primary/20'>
            <div className='sticky top-4 max-h-none'>
              {context.title && (
                <h2 className='text-lg font-semibold text-gray-800 dark:text-white mb-4'>
                  {context.type === 'latex' ? <MathText text={context.title} /> : context.title}
                </h2>
              )}

              {(context.imageUrl || context.imageBase64) && (
                <img
                  src={context.imageUrl || context.imageBase64}
                  alt={context.title || 'Context image'}
                  className='w-full max-h-80 object-contain mb-4 rounded-lg'
                  onError={e => {
                    console.error('Failed to load image:', e);
                    e.currentTarget.style.display = 'none';
                  }}
                />
              )}

              {context.content && (
                <div className='text-sm text-foreground whitespace-pre-line leading-relaxed max-h-none'>
                  {context.type === 'latex' ? (
                    <MathText text={context.content} />
                  ) : (
                    <HtmlContent content={context.content} />
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Questions Section - Right Side */}
        <div className='flex-1 min-w-0'>
          <div className='space-y-6'>
            {/* Statements Table */}
            <div className='border border-border rounded-lg overflow-hidden shadow-sm group hover:shadow-lg transition-all duration-300'>
              <table className='w-full table-fixed'>
                <thead className='bg-muted'>
                  <tr>
                    <th className='text-left p-4 text-sm font-semibold text-foreground border-r border-border w-[calc(100%-160px)]'>
                      {title ? (
                        <HtmlContent
                          content={title}
                          className='text-sm font-semibold text-foreground'
                        />
                      ) : (
                        t('landing.questions.trueFalse.statements')
                      )}
                    </th>
                    <th className='text-center p-4 text-sm font-semibold text-foreground w-20 border-r border-border bg-card'>
                      {headerTrueLabel}
                    </th>
                    <th className='text-center p-4 text-sm font-semibold text-foreground w-20 bg-card'>
                      {headerFalseLabel}
                    </th>
                  </tr>
                </thead>
                <tbody className='bg-card/80'>
                  {items.map((item, index) => (
                    <tr
                      key={item.id}
                      className={cn('border-t border-border transition-colors', 'hover:bg-muted')}
                    >
                      <td className='p-4 border-r border-border w-[calc(100%-160px)] align-top'>
                        <div className='flex items-start gap-3'>
                          <span className='flex-shrink-0 w-6 h-6 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded-full flex items-center justify-center text-xs font-semibold'>
                            {item.label || index + 1}
                          </span>
                          <div className='text-sm text-foreground leading-relaxed pt-0.5 break-words flex-1 min-w-0'>
                            {context?.type === 'latex' ? (
                              <MathText text={item.statement} />
                            ) : (
                              <HtmlContent
                                content={item.statement}
                                className='prose prose-sm max-w-none break-words [&_*]:break-words'
                              />
                            )}
                          </div>
                        </div>
                      </td>
                      <td className='text-center p-4 border-r border-border bg-card'>
                        <div className='flex items-center justify-center'>
                          <button
                            type='button'
                            onClick={() => handleItemChange(item.id, true)}
                            disabled={readOnly}
                            className={cn(
                              'w-8 h-8 rounded-md border flex items-center justify-center transition-all duration-300',
                              value && value[item.id] === true
                                ? 'bg-primary text-primary-foreground border-primary shadow'
                                : 'border-border hover:border-primary hover:bg-primary/10',
                              readOnly && 'opacity-50 cursor-not-allowed'
                            )}
                            aria-label='Mark as True'
                          >
                            <span className='text-xs font-semibold'>{buttonTrueLabel}</span>
                          </button>
                        </div>
                      </td>
                      <td className='text-center p-4 bg-card'>
                        <div className='flex items-center justify-center'>
                          <button
                            type='button'
                            onClick={() => handleItemChange(item.id, false)}
                            disabled={readOnly}
                            className={cn(
                              'w-8 h-8 rounded-md border flex items-center justify-center transition-all duration-300',
                              value && value[item.id] === false
                                ? 'bg-destructive text-destructive-foreground border-destructive shadow'
                                : 'border-border hover:border-destructive hover:bg-destructive/10',
                              readOnly && 'opacity-50 cursor-not-allowed'
                            )}
                            aria-label='Mark as False'
                          >
                            <span className='text-xs font-semibold'>{buttonFalseLabel}</span>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Scoring Info - Only show for VSAT exams */}
            {!readOnly && examStandard === 'VSAT' && (
              <Card className='p-3 bg-primary/10 border-primary/30 group hover:shadow-lg transition-all duration-300 hover:ring-2 hover:ring-primary/20'>
                <div className='text-xs text-primary dark:text-primary space-y-1'>
                  <div>
                    <strong>{t('landing.questions.trueFalse.scoring.title')}</strong>
                  </div>
                  <div>{t('landing.questions.trueFalse.scoring.oneCorrect')}</div>
                  <div>{t('landing.questions.trueFalse.scoring.twoCorrect')}</div>
                  <div>{t('landing.questions.trueFalse.scoring.threeCorrect')}</div>
                  <div>{t('landing.questions.trueFalse.scoring.fourCorrect')}</div>
                </div>
              </Card>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
